import {z} from 'zod'
import {PaginationResponse, TableFiltersPayload, TableOrder} from '@/types.ts'

/**
 * Async export domain types.
 *
 * NOTE: Response-object fields that originate from the backend (entity / status / format)
 * are kept as permissive `z.string()` so that an unexpected value (e.g. a not-yet-aligned
 * `accesses_rendiconto` key, or a future status) never makes the whole list fail to parse.
 * The strongly-typed unions below drive the UI logic and the request payloads we send.
 */

/* ------------------------------------------------------------------ */
/* Canonical enums (what the FE sends / knows about)                   */
/* ------------------------------------------------------------------ */

export const EXPORT_ENTITIES = [
    'patients',
    'patient_events',
    'treatment_requests',
    'projects',
    'accesses_raw',
    'activity_report',
    'accesses_ria',
    'users'
] as const
export const ExportEntity = z.enum(EXPORT_ENTITIES)
export type ExportEntity = z.infer<typeof ExportEntity>

/** Sentinel for the "all entities" tab — no `entity` filter is sent to the backend. */
export const ALL_ENTITIES = 'all'
export type ExportTabSelection = ExportEntity | typeof ALL_ENTITIES

export const EXPORT_STATUSES = ['pending', 'processing', 'ready', 'failed', 'archived', 'cancelled', 'expired'] as const
export const ExportJobStatus = z.enum(EXPORT_STATUSES)
export type ExportJobStatus = z.infer<typeof ExportJobStatus>

export const EXPORT_FORMATS = ['xlsx', 'xls', 'csv', 'txt', 'pdf'] as const
export const ExportFormat = z.enum(EXPORT_FORMATS)
export type ExportFormat = z.infer<typeof ExportFormat>

/**
 * Statuses a user can explicitly filter the history by. Excludes `archived` (separate toggle) and
 * `cancelled` (soft-deleted server-side, so cancelled jobs are never returned by the backend).
 */
export const EXPORT_STATUS_FILTERS = ['pending', 'processing', 'ready', 'failed'] as const
export type ExportStatusFilter = (typeof EXPORT_STATUS_FILTERS)[number]

/* ------------------------------------------------------------------ */
/* Applied filters (echoed back by the backend, ID fields resolved)    */
/* ------------------------------------------------------------------ */

export const ResolvedId = z.object({
    id: z.string(),
    label: z.string()
})
export type ResolvedId = z.infer<typeof ResolvedId>

/** A single applied-filter field. `value` stays `unknown`: the renderer decides scalar vs resolved-id. */
export const AppliedFilterField = z.object({
    key: z.string(),
    operator: z.string(),
    value: z.unknown()
})
export type AppliedFilterField = z.infer<typeof AppliedFilterField>

export const AppliedFiltersPayload = z.object({
    operator: z.enum(['and', 'or']),
    fields: z.array(AppliedFilterField)
})
export type AppliedFiltersPayload = z.infer<typeof AppliedFiltersPayload>

/* ------------------------------------------------------------------ */
/* Export job (the core record)                                        */
/* ------------------------------------------------------------------ */

export const ExportFailureReason = z.object({
    code: z.string(),
    message: z.string()
})
export type ExportFailureReason = z.infer<typeof ExportFailureReason>

export const ExportJob = z.object({
    id: z.string(),
    entity: z.string(),
    status: z.string(),
    format: z.string(),
    appliedFilters: AppliedFiltersPayload.nullish(),
    appliedColumns: z.array(z.string()).nullish(),
    selectionSummary: z.number().nullish(),
    requestedAt: z.string(),
    completedAt: z.string().nullish(),
    failedAt: z.string().nullish(),
    failureReason: ExportFailureReason.nullish(),
    archivedAt: z.string().nullish(),
    cancelledAt: z.string().nullish(),
    fileName: z.string().nullish(),
    fileSizeBytes: z.number().nullish(),
    downloadUrl: z.string().nullish()
})
export type ExportJob = z.infer<typeof ExportJob>

/* ------------------------------------------------------------------ */
/* API response envelopes                                              */
/* ------------------------------------------------------------------ */

/**
 * Single-job endpoints (create / detail / retry / cancel / archive) return a **bare** `ExportJob`
 * (no envelope), so `ExportJob` itself is the response shape — see `parseExportJob` in the http layer.
 *
 * GET list → Adonis-style paginator: `{ meta: { total, currentPage, lastPage, perPage, ... }, data: ExportJob[] }`.
 * `PaginationResponse` validates the meta fields we use; extra meta keys (page URLs) are ignored.
 */
export const ExportJobListResponse = z.object({
    meta: PaginationResponse,
    data: z.array(ExportJob)
})
export type ExportJobListResponse = z.infer<typeof ExportJobListResponse>

/* ------------------------------------------------------------------ */
/* Request payloads / params                                           */
/* ------------------------------------------------------------------ */

export interface CreateExportPayload {
    entity: ExportEntity
    format: ExportFormat
    selectedIds: string[]
    exceptedIds: string[]
    filters?: TableFiltersPayload['filters']
    fields?: string[]
    sorts?: TableOrder[]
    /** Required when `entity === 'patient_events'` — the owning patient id. */
    scopeId?: string
}

export interface ExportsListParams {
    entity?: ExportEntity
    status?: ExportStatusFilter
    format?: ExportFormat
    dateFrom?: string
    dateTo?: string
    includeArchived?: boolean
    page?: number
    limit?: number
}

/* ------------------------------------------------------------------ */
/* Status helpers (string-tolerant)                                    */
/* ------------------------------------------------------------------ */

/** Monotonic lifecycle rank used to ignore stale / out-of-order socket events. */
const STATUS_RANK: Record<string, number> = {
    pending: 0,
    processing: 1,
    ready: 2,
    failed: 2,
    cancelled: 3,
    archived: 3,
    expired: 3
}

export const exportStatusRank = (status: string): number => STATUS_RANK[status] ?? -1

export const canDownloadExport = (job: ExportJob): boolean => job.status === 'ready'
export const canRetryExport = (job: ExportJob): boolean => job.status === 'failed'
export const canCancelExport = (job: ExportJob): boolean => job.status === 'pending' || job.status === 'processing'
export const canArchiveExport = (job: ExportJob): boolean => job.status === 'ready'

/** Terminal jobs that can be permanently removed from the history via DELETE. */
export const canDeleteExport = (job: ExportJob): boolean =>
    job.status === 'cancelled' || job.status === 'failed' || job.status === 'archived'
